/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, Col, Row } from 'reactstrap'
import CustomModal from '../../../../common/CustomModal/CustomModal'
import CustomNumberFormat from '../../../../common/CustomNumberFormat'
import CustomSelect from '../../../../common/CustomReactSelectComponent'
import CustomPrimeTable, { DEFAULT_PAGE_SAZE } from '../../../../common/PrimeTable/CustomPrimeTable'
import { OUT_GOING_CALL } from '../../../../constants/activityTypes'
import { getStatus } from '../../../../constants/statuses'
import { ORGANIZATION_TYPE } from '../../../../constants/supportTypes'
import useGetSupportTable from '../../../../hooks/useGetSupportTable'
import camelToSnake from '../../../../utils/camelToSnake'
import Loader from '../../../Loader'
import { clearPossibleContacts, createNewContact, getPossibleContacts, mergeContact } from '../redux/applicationActions'

const DEFAULT_SORT_META = [{ field: 'creation_date', order: 1 }]
const DEFAULT_PAGINATION = { limit: DEFAULT_PAGE_SAZE, first: 0 }

/**
 * @param {number} [linkedContactId] - contact the application is already linked to, when the modal
 *        was opened to change that link rather than create one. The row for that contact is marked
 *        instead of offering a button, so re-picking it — a no-op that still re-runs the CPS data
 *        merge — is not on the table.
 * @param {string} [linkedContactName] - shown above the results. The modal covers the row it was
 *        opened from, so without this there is nothing on screen saying what the current link is,
 *        and the search is by the application's own details, which need not match that contact.
 */
export default function PossibleContacts({ searchBy, applicantData, callback, linkedContactId, linkedContactName }) {
  const { loading, contacts, query } = useSelector((state) => ({
    loading: state.creditApplications.loading,
    contacts: state.creditApplications.contacts.data,
    query: state.creditApplications.contacts.query,
  }))

  const dispatch = useDispatch()

  const [options, setOptions] = useState({
    pagination: DEFAULT_PAGINATION,
    sort: { id: 1 },
    filter: {},
    tableFilter: {},
  })

  const [init, setInit] = useState(false)
  const [open, setOpen] = useState(false)

  const [newContactData, setNewContactData] = useState({})

  const organizationTypeOptions = useGetSupportTable(ORGANIZATION_TYPE)

  useEffect(() => {
    setOptions((prev) => ({
      ...prev,
      filter: searchBy,
    }))
    setInit(true)
  }, [searchBy])

  useEffect(() => {
    if (init) {
      dispatch(getPossibleContacts(options))
    }
  }, [init, options])

  useEffect(() => {
    if (applicantData) {
      const {
        legalBusinessName: name,
        dbaName: dba,
        contactPerson,
        title: contactPersonTitle,
        email,
        businessPhone,
        mobilePhone,
        website,
        dotNumber,
        mcNumber,
        numberOfTrucks,
        numberOfDrivers,
        zip,
        street,
        city,
      } = applicantData

      setNewContactData((prev) => ({
        ...prev,
        email,
        name,
        dba,
        businessPhone,
        mobilePhone,
        website,
        dotNumber,
        mcNumber,
        numberOfTrucks,
        numberOfDrivers,
        contactPerson,
        contactPersonTitle,
        addressZip: zip,
        //state
        addressStreet1: street,
        addressCity: city,
      }))
    }

    return () => {
      dispatch(clearPossibleContacts())
    }
  }, [applicantData])

  const toggleModal = () => {
    setOpen((prev) => !prev)
  }

  const handleSelectChange = (values, e) => {
    const { name } = e
    setNewContactData((prev) => ({ ...prev, [name]: values ? values.id : null }))
  }

  const handleContactCreate = () => {
    dispatch(createNewContact(applicantData.id, newContactData, callback))
    toggleModal()
  }

  const handleContactMerge = (contactId) => {
    const data = { contactId }
    dispatch(mergeContact(applicantData.id, data, callback))
  }

  const sortMeta = () => {
    if (options?.sort) {
      const field = Object.keys(options?.sort)[0]
      const order = options?.sort[field]

      return [
        {
          field,
          order,
        },
      ]
    } else {
      return DEFAULT_SORT_META
    }
  }

  const handleSortChange = (event) => {
    let sort = {}
    if (event.multiSortMeta) {
      event.multiSortMeta.map((row) => {
        return (sort[row.field] = row.order)
      })
    }

    setOptions((prev) => ({
      ...prev,
      sort,
      pagination: DEFAULT_PAGINATION,
    }))
  }

  const handlePageChange = (event) => {
    const pagination = { limit: event.rows, first: event.first }
    setOptions((prev) => ({ ...prev, pagination }))
  }

  const handleFilterChange = (event) => {
    let filter = {}

    // add filters handled outside of the prime table
    const { creation_date, business_phone } = options.filter

    if (creation_date?.from || creation_date?.to) {
      filter = { ...filter, creation_date }
    }
    if (business_phone) {
      filter = { ...filter, business_phone }
    }

    Object.keys(event.filters).forEach(function (key) {
      filter[camelToSnake(key)] = event.filters[key].value
    })

    setOptions((prev) => ({ ...prev, filter: { ...filter } }))
  }

  const rightButtons = (selectedRow) => {
    return (
      <>
        <Row>
          <Col className={'d-flex justify-content-end'}>
            <Button size='sm' className='gold-color-button' onClick={toggleModal}>
              Add As New Contact
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  const contactNameBody = (rowData) => {
    return (
      <Link onClick={() => window.open(`/contacts/${rowData.id}`, '_blank')} rel='noopener noreferrer'>
        {rowData.name}
      </Link>
    )
  }

  const assignedBody = (rowData) => {
    return <div>{rowData?.userData?.name}</div>
  }

  const addressBody = (rowData) => {
    return (
      <div>
        {rowData.addressData?.street ? `${rowData.addressData?.street}` : ''}
        {rowData.addressData?.city ? `, ${rowData.addressData?.city}` : ''}
        {rowData.addressData?.state ? `, ${rowData.addressData?.state}` : ''}
      </div>
    )
  }

  const statusBody = (rowData) => {
    return <div>{getStatus(rowData.statusId)}</div>
  }

  const businessPhoneBody = (rowData) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <CustomNumberFormat
          className={'form-control-sm form-control'}
          name='office_phone'
          value={rowData.businessPhone || ''}
          placeholder='Office Phone'
          type='tel'
          disabled
          format='(999) 999-9999'
          size={10}
          contactId={rowData.id}
          contactName={rowData.name}
          activityType={OUT_GOING_CALL}
        />
      </div>
    )
  }

  const mergeContactBody = (rowData) => {
    if (linkedContactId && rowData.id === linkedContactId) {
      return <div className='text-muted'>Currently linked</div>
    }

    return (
      <div>
        <Button
          size='sm'
          className='gold-color-button'
          onClick={() => {
            handleContactMerge(rowData.id)
          }}
        >
          {linkedContactId ? 'Link Here' : 'Merge'}
        </Button>
      </div>
    )
  }

  const columns = [
    {
      field: 'name',
      header: 'Name',
      body: contactNameBody,
      style: { textAlign: 'center' },
    },
    { field: 'statusId', header: 'Status', style: { textAlign: 'center' }, body: statusBody },
    { field: 'u.name', header: 'Assigned', style: { textAlign: 'center' }, body: assignedBody },
    { field: 'ownerName', header: 'Owner', style: { textAlign: 'center' } },
    {
      field: 'c.address_city',
      header: 'Address',
      body: addressBody,
      style: { textAlign: 'center' },
    },
    { field: 'email', header: 'Email', style: { textAlign: 'center' } },
    {
      field: 'businessPhone',
      header: 'Business Phone',
      body: businessPhoneBody,
      style: { textAlign: 'center', width: 'max-content' },
    },
    {
      field: 'merge',
      header: linkedContactId ? 'Link' : 'Merge',
      sortable: false,
      body: mergeContactBody,
      style: { textAlign: 'center' },
    },
  ]

  return (
    <>
      {loading && <Loader />}
      <CustomModal isOpen={open} toggle={() => toggleModal()} headerText={'Add Contact'} medium>
        <Row className='mb-5 d-flex justify-content-center'>
          <Col md={8}>
            <CustomSelect
              label='Select Organization Type'
              onChange={(values, event) => {
                handleSelectChange(values, event)
              }}
              options={organizationTypeOptions || []}
              name='organizationTypeId'
              defaultValue={newContactData?.organizationTypeId}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              className='btn btn-gold float-right m-1'
              disabled={!newContactData?.organizationTypeId}
              onClick={handleContactCreate}
            >
              Save
            </Button>
          </Col>
        </Row>
      </CustomModal>
      {linkedContactId && (
        <Row>
          <Col>
            <div className='mb-2'>
              Currently linked to <strong>{linkedContactName || `contact #${linkedContactId}`}</strong>. Pick another
              contact below to move this application to it.
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <Card>
            <CardBody>
              <CustomPrimeTable
                findHidden
                data={contacts}
                columns={columns}
                addHidden
                showActions={false}
                query={query}
                onSort={handleSortChange}
                onPage={handlePageChange}
                onFilter={handleFilterChange}
                defaultSortMeta={sortMeta()}
                rightButtons={rightButtons}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}
